import {
    Action,
    IAgentRuntime,
    Memory,
    State,
    HandlerCallback,
    elizaLogger,
} from "@elizaos/core";
import { initCotiWalletProvider } from "../providers/wallet";
import { ethers } from "ethers";
import { z } from "zod";

// Schema for deployment parameters
export const DeployPrivateERC721Schema = z.object({
    name: z.string().describe("Name of the NFT collection"),
    symbol: z.string().describe("Symbol of the NFT collection"),
    gasLimit: z.number().optional().describe("Optional gas limit"),
});

export type DeployPrivateERC721Params = z.infer<typeof DeployPrivateERC721Schema>;

// Basic ERC721 ABI for deployment
const ERC721_ABI = [
    "constructor(string name, string symbol)",
    "function name() view returns (string)",
    "function symbol() view returns (string)",
    "function totalSupply() view returns (uint256)",
    "function balanceOf(address owner) view returns (uint256)",
    "function ownerOf(uint256 tokenId) view returns (address)",
    "function mint(address to, string uri) returns (uint256)",
    "function tokenURI(uint256 tokenId) view returns (string)"
];

// Basic ERC721 bytecode (simplified)
const ERC721_BYTECODE = "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";

export const deployPrivateERC721ContractAction: Action = {
    name: "DEPLOY_PRIVATE_ERC721_CONTRACT",
    similes: [
        "DEPLOY_ERC721",
        "CREATE_NFT",
        "DEPLOY_NFT",
        "CREATE_PRIVATE_NFT",
        "DEPLOY_PRIVATE_NFT",
        "CREATE_NFT_COLLECTION",
        "DEPLOY_NFT_COLLECTION"
    ],
    description: "Deploy a new private ERC721 NFT contract on the COTI blockchain. This creates a new private NFT collection with the specified name and symbol.",
    validate: async (runtime: IAgentRuntime, message: Memory): Promise<boolean> => {
        try {
            const text = message.content?.text || "";
            elizaLogger.info(`DEPLOY_PRIVATE_ERC721_CONTRACT validation - text: "${text}"`);
            
            // Check for deployment keywords
            const isDeployQuery = /\b(deploy|create|new).*\b(nft|erc721|collection)\b/i.test(text);
            elizaLogger.info(`DEPLOY_PRIVATE_ERC721_CONTRACT validation - isDeployQuery: ${isDeployQuery}`);
            
            // Check if message content has name and symbol fields
            if (message.content?.name && message.content?.symbol) {
                elizaLogger.info(`DEPLOY_PRIVATE_ERC721_CONTRACT validation - structured params found`);
                return true;
            }

            // Check if text contains name and symbol patterns
            const nameMatch = text.match(/name\s*['""]([^'"]*)['""]|called\s*['""]([^'"]*)['"]/i);
            const symbolMatch = text.match(/symbol\s*['""]([^'"]*)['""]|\(([^)]+)\)/i);
            
            elizaLogger.info(`DEPLOY_PRIVATE_ERC721_CONTRACT validation - nameMatch: ${nameMatch?.[1] || nameMatch?.[2] || 'none'}`);
            elizaLogger.info(`DEPLOY_PRIVATE_ERC721_CONTRACT validation - symbolMatch: ${symbolMatch?.[1] || symbolMatch?.[2] || 'none'}`);

            if (isDeployQuery && nameMatch && symbolMatch) {
                elizaLogger.info(`DEPLOY_PRIVATE_ERC721_CONTRACT validation - returning true`);
                return true;
            }

            elizaLogger.info(`DEPLOY_PRIVATE_ERC721_CONTRACT validation - returning false`);
            return false;
        } catch (error) {
            elizaLogger.error(`DEPLOY_PRIVATE_ERC721_CONTRACT validation error:`, error);
            return false;
        }
    },
    handler: async (
        runtime: IAgentRuntime,
        message: Memory,
        state: State | undefined,
        options: any = {},
        callback?: HandlerCallback
    ) => {
        try {
            elizaLogger.info("Starting DEPLOY_PRIVATE_ERC721_CONTRACT action");

            let params: DeployPrivateERC721Params;

            // Try to get parameters from message content
            if (message.content?.name && message.content?.symbol) {
                params = DeployPrivateERC721Schema.parse(message.content);
            } else {
                // Extract from message text
                const text = message.content?.text || "";
                const nameMatch = text.match(/name\s*['""]([^'"]*)['""]|called\s*['""]([^'"]*)['"]/i);
                const symbolMatch = text.match(/symbol\s*['""]([^'"]*)['""]|\(([^)]+)\)/i);
                
                if (!nameMatch || !symbolMatch) {
                    throw new Error("Could not extract name and symbol from message");
                }
                
                params = {
                    name: (nameMatch[1] || nameMatch[2]).trim(),
                    symbol: (symbolMatch[1] || symbolMatch[2]).trim()
                };
            }

            const walletProvider = initCotiWalletProvider(runtime);
            const wallet = walletProvider.getWallet();

            // Create contract factory
            const factory = new ethers.ContractFactory(
                ERC721_ABI,
                ERC721_BYTECODE,
                wallet
            );

            // Deploy the contract
            elizaLogger.info(`Deploying NFT contract with name: ${params.name}, symbol: ${params.symbol}`);

            const contract = await factory.deploy(
                params.name,
                params.symbol,
                {
                    gasLimit: params.gasLimit || 2000000
                }
            );

            // Wait for deployment
            const deploymentTransaction = contract.deploymentTransaction();
            if (!deploymentTransaction) {
                throw new Error("Deployment transaction not found");
            }

            const contractAddress = await contract.getAddress();
            const receipt = await deploymentTransaction.wait();

            const responseText = `Successfully deployed private ERC721 contract: ${params.name} (${params.symbol})
Contract Address: ${contractAddress}
Transaction Hash: ${deploymentTransaction.hash}`;

            if (callback) {
                await callback({
                    text: responseText,
                    content: {
                        success: true,
                        contractAddress,
                        transactionHash: deploymentTransaction.hash,
                        name: params.name,
                        symbol: params.symbol,
                    },
                });
            }

            return {
                text: responseText,
                success: true,
                data: {
                    contractAddress,
                    transactionHash: deploymentTransaction.hash,
                    name: params.name,
                    symbol: params.symbol,
                },
            };
        } catch (error) {
            elizaLogger.error("Error in DEPLOY_PRIVATE_ERC721_CONTRACT action:", error);
            const errorMessage = `Failed to deploy private ERC721 contract: ${error instanceof Error ? error.message : String(error)}`;

            if (callback) {
                await callback({
                    text: errorMessage,
                    content: { success: false, error: errorMessage },
                });
            }

            return {
                text: errorMessage,
                success: false,
                error: error instanceof Error ? error : new Error(String(error)),
            };
        }
    },
    examples: [
        [
            {
                name: "{{user1}}",
                content: {
                    text: "Deploy a new NFT collection called 'My Art Gallery' with symbol 'ART'",
                },
            },
            {
                name: "{{user2}}",
                content: {
                    text: "I'll deploy a private ERC721 NFT collection for you on the COTI blockchain.",
                    action: "DEPLOY_PRIVATE_ERC721_CONTRACT",
                },
            },
        ],
    ],
};
